import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Var() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🏷 JavaScript var Keyword</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <b>var</b> keyword is the oldest way to declare variables in
        JavaScript. It was used before <code>let</code> and <code>const</code>{" "}
        were introduced in ES6 (2015). While it still works, it has several
        drawbacks — mainly due to <b>function-scoping</b> and{" "}
        <b>hoisting behavior</b>.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
        }}
      >
        <h2 style={{ color: "#0369a1" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Variables declared with <code>var</code> are <b>function-scoped</b>.
          </li>
          <li>
            <code>var</code> variables are <b>hoisted</b> to the top of their
            scope.
          </li>
          <li>
            They can be <b>redeclared</b> and <b>updated</b> within the same
            scope.
          </li>
          <li>
            Best practice: use <code>let</code> or <code>const</code> instead.
          </li>
        </ul>
      </div>

      {/* Example 1: Basic var Declaration */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic var Declaration</h2>
        <p>
          You can declare a variable using <code>var</code> and assign a value
          to it.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`var name = "Edu-Tech Master Academy";
console.log(name);`}
          />
          <TryItButton
            code={`var name = "Edu-Tech Master Academy";
console.log(name);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          Output: <b>Edu-Tech Master Academy</b>
        </div>
      </div>

      {/* Example 2: Redeclaring Variables */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Redeclaring Variables</h2>
        <p>
          Unlike <code>let</code> and <code>const</code>, variables declared
          with <code>var</code> can be <b>redeclared</b> in the same scope
          without an error.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`var name = "Edu-Tech Master Academy";
var name = "ETMA JavaScript Course";
console.log(name);`}
          />
          <TryItButton
            code={`var name = "Edu-Tech Master Academy";
var name = "ETMA JavaScript Course";
console.log(name);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          Output: <b>ETMA JavaScript Course</b>
        </div>
      </div>

      {/* Example 3: Function Scope */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Function Scope</h2>
        <p>
          A <code>var</code> variable declared inside a function is accessible
          only inside that function.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`function demo() {
  var course = "JavaScript Basics";
  console.log(course);
}

demo();
// console.log(course); // ❌ Error: course is not defined`}
          />
          <TryItButton
            code={`function demo() {
  var course = "JavaScript Basics";
  console.log(course);
}

demo();`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #f59e0b",
            borderRadius: "8px",
          }}
        >
          Output: <b>JavaScript Basics</b>
        </div>
      </div>

      {/* Example 4: var and Hoisting */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ var and Hoisting</h2>
        <p>
          Variables declared with <code>var</code> are <b>hoisted</b> to the top
          of their scope, meaning you can use them before declaring — but their
          value will be <code>undefined</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log(name); // undefined
var name = "Edu-Tech Master Academy";
console.log(name);`}
          />
          <TryItButton
            code={`console.log(name);
var name = "Edu-Tech Master Academy";
console.log(name);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #ef4444",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            undefined
            <br />
            Edu-Tech Master Academy
          </pre>
        </div>
      </div>

      {/* Example 5: var is Not Block Scoped */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ var is Not Block Scoped</h2>
        <p>
          Unlike <code>let</code> and <code>const</code>, <code>var</code> is
          not limited to block scope. It leaks out of <code>{`{}`}</code>{" "}
          blocks.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`if (true) {
  var name = "Edu-Tech Master Academy";
}
console.log(name); // accessible outside the block`}
          />
          <TryItButton
            code={`if (true) {
  var name = "Edu-Tech Master Academy";
}
console.log(name);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #9333ea",
            borderRadius: "8px",
          }}
        >
          Output: <b>Edu-Tech Master Academy</b>
        </div>
      </div>

      {/* Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            ✅ Prefer <code>let</code> and <code>const</code> over{" "}
            <code>var</code>.
          </li>
          <li>
            ✅ Understand <b>hoisting</b> when working with <code>var</code>.
          </li>
          <li>
            ⚠ Avoid using <code>var</code> in modern code — it can cause
            unexpected behavior.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>let</b> and <b>const</b> for better variable
            management.
          </li>
          <li>
            Explore <b>JavaScript Scope</b> and <b>Hoisting</b> deeper.
          </li>
          <li>
            Understand the difference between <b>Global</b> and <b>Local</b>{" "}
            variables.
          </li>
        </ul>
      </div>
    </div>
  );
}
